import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUp, History, MessageSquareText, Plus, Sparkles, Square, X } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { useAi } from "@/ai/AiContext";
import { useAiChat } from "@/ai/useAiChat";
import { GoogleButton } from "@/components/GoogleButton";

const starters = ["Nifty aaj kaisa raha aur kyun?", "Explain candlesticks like I'm 12", "What is risk management in one minute?", "Gold aur crude ka Indian market pe kya asar hota hai?"];
const bold = (s) => s.split(/(\*\*[^*]+\*\*)/g).map((p, j) => p.startsWith("**") ? <b key={`${j}-${p.slice(2, 10)}`}>{p.slice(2, -2)}</b> : p);

function Markdownish({ text }) {
  return <div className="ai-md">{text.split("\n").map((l, i) => {
    const key = `${i}-${l.slice(0, 12)}`;
    if (/^\s*[-•*]\s+/.test(l)) return <div className="ai-li" key={key}><span>–</span><span>{bold(l.replace(/^\s*[-•*]\s+/, ""))}</span></div>;
    if (!l.trim()) return <div className="ai-gap" key={key} />;
    return <p key={key}>{bold(l.replace(/^#+\s*/, ""))}</p>;
  })}</div>;
}

function Bubble({ m }) {
  return <div className={`ai-msg ${m.role}`} data-testid={`ai-message-${m.role}`}>{m.pending ? <span className="ai-typing"><i /><i /><i /></span> : <Markdownish text={m.content} />}</div>;
}

function AiHeader({ user, chat, showHist, setShowHist, close }) {
  return <header className="ai-head">
    <div className="ai-title"><span className="ai-orb" /><div><b>Arijit AI</b><small>Market learning assistant · Claude</small></div></div>
    <div className="ai-head-actions">
      {user && <button className="icon-btn" onClick={() => setShowHist(!showHist)} aria-label="History" data-testid="ai-history-button"><History size={15} /></button>}
      {user && <button className="icon-btn" onClick={() => { chat.newChat(); setShowHist(false); }} aria-label="New chat" data-testid="ai-new-chat-button"><Plus size={15} /></button>}
      <button className="icon-btn" onClick={close} aria-label="Close" data-testid="ai-close-button"><X size={15} /></button>
    </div>
  </header>;
}

function AiLocked() {
  return <div className="ai-locked" data-testid="ai-locked"><MessageSquareText size={28} /><b>Members ask, Arijit AI teaches.</b><p>Ask anything about candles, risk, options basics or today's market moves — in English, Hindi or Hinglish. Free with Google sign-in. Never buy/sell calls, always learning.</p><GoogleButton label="Sign in free to ask" testId="ai-signin-button" /></div>;
}

function AiHistory({ chat, onPick }) {
  if (!chat.sessions.length) return <div className="ai-hist" data-testid="ai-history"><p>No past chats yet.</p></div>;
  return <div className="ai-hist" data-testid="ai-history">{chat.sessions.map((s) => <button key={s.session_id} className={s.session_id === chat.sessionId ? "active" : ""} onClick={() => onPick(s.session_id)} data-testid={`ai-session-${s.session_id}`}>{s.title}</button>)}</div>;
}

function AiComposer({ chat, input, setInput }) {
  const submit = (e) => { e?.preventDefault(); if (!input.trim()) return; chat.send(input); setInput(""); };
  return <form className="ai-input" onSubmit={submit}>
    <textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) submit(e); }} placeholder="Ask in English, Hindi or Hinglish…" rows={1} maxLength={1500} data-testid="ai-input" />
    {chat.streaming
      ? <button type="button" className="ai-send stop" onClick={chat.stop} aria-label="Stop" data-testid="ai-stop-button"><Square size={14} /></button>
      : <button className="ai-send" disabled={!input.trim()} aria-label="Send" data-testid="ai-send-button"><ArrowUp size={16} /></button>}
  </form>;
}

function AiConversation({ user, chat, input, setInput, showHist, setShowHist }) {
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chat.messages]);
  return <>
    {showHist && <AiHistory chat={chat} onPick={(id) => { chat.switchTo(id); setShowHist(false); }} />}
    <div className="ai-body" data-testid="ai-messages">
      {!chat.messages.length && <div className="ai-welcome"><p>Namaste {user.name?.split(" ")[0]}. I teach, I don't tip. Ask me anything about markets — with today's live numbers in mind.</p><div className="ai-starters">{starters.map((s) => <button key={s} onClick={() => chat.send(s)} data-testid="ai-starter">{s}</button>)}</div></div>}
      {chat.messages.map((m) => <Bubble m={m} key={m.id} />)}
      {chat.error && <div className="ai-error" data-testid="ai-error">{chat.error}</div>}
      <div ref={endRef} />
    </div>
    <AiComposer chat={chat} input={input} setInput={setInput} />
    <p className="ai-foot">Learning only · not investment advice · not SEBI registered</p>
  </>;
}

export function AiDrawer() {
  const { user } = useAuth();
  const { open, setOpen, seed, setSeed } = useAi();
  const chat = useAiChat(!!user && open);
  const [input, setInput] = useState("");
  const [showHist, setShowHist] = useState(false);

  useEffect(() => { if (seed) { setInput(seed); setSeed(""); } }, [seed, setSeed]);
  useEffect(() => { const k = (e) => e.key === "Escape" && setOpen(false); window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k); }, [setOpen]);

  return <>
    <motion.button className="ai-fab" onClick={() => setOpen(true)} data-testid="ai-fab" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.8 }} aria-label="Ask Arijit AI"><Sparkles size={17} /><span>Ask Arijit AI</span></motion.button>
    <AnimatePresence>{open && <>
      <motion.div className="ai-backdrop" onClick={() => setOpen(false)} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} />
      <motion.aside className="ai-drawer" data-testid="ai-drawer" initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "spring", stiffness: 260, damping: 30 }}>
        <AiHeader user={user} chat={chat} showHist={showHist} setShowHist={setShowHist} close={() => setOpen(false)} />
        {user ? <AiConversation user={user} chat={chat} input={input} setInput={setInput} showHist={showHist} setShowHist={setShowHist} /> : <AiLocked />}
      </motion.aside>
    </>}</AnimatePresence>
  </>;
}
